"use client";

import { useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, INPUT_MONO, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type MeasurementRow,
  MeasurementRows,
} from "@/components/tools/math/measurement-rows";
import { UncertaintyResults } from "@/components/tools/math/uncertainty-results";
import {
  formulaView,
  powerView,
  productView,
  readFormula,
  sumView,
  type VariableInput,
  type ViewOutcome,
} from "@/components/tools/math/uncertainty-views";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { AngleUnit } from "@/lib/tool-kit/math/expression";
import type { UncertaintyRounding } from "@/lib/tool-kit/math/uncertainty";

/** English strings. A translated content file overrides these through `ui`. */
export const UNCERTAINTY_PROPAGATION_CALCULATOR_STRINGS = {
  modeLabel: "How is the result calculated?",
  modeSum: "Sum or difference",
  modeProduct: "Product or quotient",
  modePower: "Power",
  modeFormula: "Any formula",
  sumHint:
    "q = a ± b ± …: the absolute uncertainties add in quadrature, δq = √(δa² + δb² + …).",
  productHint:
    "q = a × b ÷ c …: the relative uncertainties add in quadrature, δq/|q| = √((δa/a)² + (δb/b)² + …).",
  powerHint:
    "q = xⁿ: the relative uncertainty is multiplied by |n|, δq/|q| = |n| × δx/|x|.",
  formulaHint:
    "Type the formula with variables. Use + − * / ^ and brackets, sqrt, sin, cos, tan, asin, acos, atan, ln, log10, exp, abs, pi and e. Write numbers with a decimal point.",
  rowLabel: "Measurement {n}",
  operation: "Operation for measurement {n}",
  value: "Value",
  uncertainty: "± Uncertainty",
  removeRow: "Remove measurement {n}",
  addRow: "Add measurement",
  plus: "+",
  minus: "−",
  times: "×",
  divide: "÷",
  base: "x (value)",
  baseUncertainty: "δx (uncertainty in x)",
  exponent: "n (power)",
  formula: "Formula",
  readAs: "Read as: {formula}",
  variablesHeading: "Values and uncertainties",
  variableValue: "{name} value",
  variableUncertainty: "± uncertainty in {name}",
  angleUnit: "Angles in trigonometric functions",
  radians: "Radians",
  degrees: "Degrees",
  rounding: "Round the uncertainty to",
  roundingOneOrTwo: "1 significant figure (2 if it starts with 1)",
  roundingTwo: "2 significant figures",
  results: "Results",
  result: "Result",
  relative: "Relative uncertainty",
  unrounded: "Unrounded",
  notApplicable: "—",
  copyResult: "Copy result",
  copied: "Copied!",
  contributionsHeading: "Where the uncertainty comes from",
  inputColumn: "Input",
  valueColumn: "Value ± uncertainty",
  sensitivityColumn: "Sensitivity ∂q/∂x",
  termColumn: "Contribution |∂q/∂x| × δx",
  shareColumn: "Share of the variance",
  workingHeading: "The working",
  roundedOneOrTwo:
    "Rounded: the uncertainty to one significant figure (two when it starts with 1), and the value to the same decimal place: {result}.",
  roundedTwo:
    "Rounded: the uncertainty to two significant figures, and the value to the same decimal place: {result}.",
  nonlinear:
    "The formula curves noticeably across ± the uncertainty in {name}, so this first-order estimate may be off. Reduce that uncertainty, or check the result with a Monte Carlo simulation.",
  assumptions:
    "Assumes the uncertainties are independent (uncorrelated) and small enough that the formula is close to a straight line across each ± range — first-order propagation, as in introductory lab manuals and the GUM.",
  enterAll: "Enter a value and an uncertainty for every input.",
  negativeUncertainty:
    "Write each uncertainty as a positive number: the ± already covers both directions.",
  productZero:
    "A value of 0 has no relative uncertainty, so the product rule cannot use it. Switch to “Any formula” to propagate through it.",
  powerDomain:
    "That power has no real value here: a negative x cannot take a fractional power, and x = 0 has no relative uncertainty.",
  notFinite:
    "The formula has no real value at these inputs, for example a square root of a negative number or a division by zero.",
  noDerivative:
    "The formula cannot be differentiated with respect to {name} at this value.",
  noVariables:
    "This formula has no variables, so there is nothing uncertain to propagate.",
  expressionErrors: {
    empty: "Type a formula, such as 4*pi^2*L/T^2.",
    "too-long": "That formula is too long; keep it under 300 characters.",
    "too-deep": "That formula is nested too deeply.",
    "unexpected-character": "“{token}” cannot be used in a formula.",
    "unexpected-token": "Something is missing or out of place near “{token}”.",
    "unexpected-end": "The formula ends too soon; is an operand missing?",
    "missing-paren": "A bracket is opened but never closed.",
    "unknown-function":
      "“{token}(” is not a known function. To multiply, write {token}*(…); otherwise use sqrt, sin, cos, tan, asin, acos, atan, ln, log10, exp or abs.",
    "ambiguous-log":
      "Write ln(x) for the natural logarithm or log10(x) for base 10: “log” means different things in different subjects.",
  },
} as const;

type Mode = "sum" | "product" | "power" | "formula";

const INITIAL_SUM: MeasurementRow[] = [
  { id: 1, op: "+", value: "10.2", uncertainty: "0.3" },
  { id: 2, op: "-", value: "4.1", uncertainty: "0.4" },
];
const INITIAL_PRODUCT: MeasurementRow[] = [
  { id: 3, op: "*", value: "5.0", uncertainty: "0.1" },
  { id: 4, op: "*", value: "3.0", uncertainty: "0.1" },
];
const INITIAL_FORMULA = "4*pi^2*L/T^2";
const INITIAL_VARIABLES: Record<string, VariableInput> = {
  L: { value: "1.000", uncertainty: "0.005" },
  T: { value: "2.007", uncertainty: "0.010" },
};

function Outcome({
  outcome,
  rule,
}: {
  outcome: ViewOutcome;
  rule: UncertaintyRounding;
}) {
  const strings = useToolUi(UNCERTAINTY_PROPAGATION_CALCULATOR_STRINGS);
  if (outcome.kind === "notice") {
    return <Notice>{outcome.message}</Notice>;
  }
  if (outcome.kind === "error") {
    return <ErrorNotice>{outcome.message}</ErrorNotice>;
  }
  return (
    <UncertaintyResults rule={rule} strings={strings} view={outcome.view} />
  );
}

function FormulaInputs({
  formula,
  setFormula,
  variables,
  setVariable,
}: {
  formula: string;
  setFormula: (value: string) => void;
  variables: Record<string, VariableInput>;
  setVariable: (name: string, patch: Partial<VariableInput>) => void;
}) {
  const strings = useToolUi(UNCERTAINTY_PROPAGATION_CALCULATOR_STRINGS);
  const parsed = readFormula(formula);
  const names = parsed.ok ? parsed.variables : [];
  return (
    <div className="space-y-4">
      <TextField
        hint={strings.formulaHint}
        id="up-formula"
        label={strings.formula}
        mono
        onChange={setFormula}
        value={formula}
      />
      {parsed.ok ? (
        <p className={`${HINT} font-mono`}>
          {t(strings.readAs, { formula: parsed.readBack })}
        </p>
      ) : null}
      {names.length > 0 ? (
        <fieldset className="space-y-3">
          <legend className="font-medium text-foreground text-sm">
            {strings.variablesHeading}
          </legend>
          {names.map((name, index) => (
            <div className="grid grid-cols-2 gap-3" key={name}>
              <div className="space-y-1">
                <label className={HINT} htmlFor={`up-var-${index}`}>
                  {t(strings.variableValue, { name })}
                </label>
                <input
                  className={INPUT_MONO}
                  id={`up-var-${index}`}
                  inputMode="decimal"
                  onChange={(e) => setVariable(name, { value: e.target.value })}
                  value={variables[name]?.value ?? ""}
                />
              </div>
              <div className="space-y-1">
                <label className={HINT} htmlFor={`up-unc-${index}`}>
                  {t(strings.variableUncertainty, { name })}
                </label>
                <input
                  className={INPUT_MONO}
                  id={`up-unc-${index}`}
                  inputMode="decimal"
                  onChange={(e) =>
                    setVariable(name, { uncertainty: e.target.value })
                  }
                  value={variables[name]?.uncertainty ?? ""}
                />
              </div>
            </div>
          ))}
        </fieldset>
      ) : null}
    </div>
  );
}

export default function UncertaintyPropagationCalculatorWidget() {
  const strings = useToolUi(UNCERTAINTY_PROPAGATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("formula");
  const [sumRows, setSumRows] = useState(INITIAL_SUM);
  const [productRows, setProductRows] = useState(INITIAL_PRODUCT);
  const [power, setPower] = useState({ x: "2.0", dx: "0.1", n: "3" });
  const [formula, setFormula] = useState(INITIAL_FORMULA);
  const [variables, setVariables] = useState(INITIAL_VARIABLES);
  const [unit, setUnit] = useState<AngleUnit>("radians");
  const [rule, setRule] = useState<UncertaintyRounding>("one-or-two");
  const nextId = useRef(100);
  const fresh = () => {
    nextId.current += 1;
    return nextId.current;
  };
  const rowLabels = {
    row: strings.rowLabel,
    operation: strings.operation,
    value: strings.value,
    uncertainty: strings.uncertainty,
    remove: strings.removeRow,
    add: strings.addRow,
  };

  let outcome: ViewOutcome = formulaView(
    formula,
    variables,
    unit,
    strings,
    fmt
  );
  if (mode === "sum") {
    outcome = sumView(sumRows, strings, fmt);
  } else if (mode === "product") {
    outcome = productView(productRows, strings, fmt);
  } else if (mode === "power") {
    outcome = powerView(power, strings, fmt);
  }
  const parsed = readFormula(formula);
  if (mode === "formula" && parsed.ok && parsed.variables.length === 0) {
    outcome = { kind: "notice", message: strings.noVariables };
  }

  const hints: Record<Mode, string> = {
    sum: strings.sumHint,
    product: strings.productHint,
    power: strings.powerHint,
    formula: "",
  };

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="up-mode"
        onChange={setMode}
        options={[
          { value: "sum", label: strings.modeSum },
          { value: "product", label: strings.modeProduct },
          { value: "power", label: strings.modePower },
          { value: "formula", label: strings.modeFormula },
        ]}
        value={mode}
      />
      {hints[mode] === "" ? null : (
        <p className="font-mono text-muted-foreground text-sm">{hints[mode]}</p>
      )}
      {mode === "sum" ? (
        <MeasurementRows
          idPrefix="up-sum"
          labels={rowLabels}
          nextId={fresh}
          onChange={setSumRows}
          ops={[
            { value: "+", label: strings.plus },
            { value: "-", label: strings.minus },
          ]}
          rows={sumRows}
        />
      ) : null}
      {mode === "product" ? (
        <MeasurementRows
          idPrefix="up-product"
          labels={rowLabels}
          nextId={fresh}
          onChange={setProductRows}
          ops={[
            { value: "*", label: strings.times },
            { value: "/", label: strings.divide },
          ]}
          rows={productRows}
        />
      ) : null}
      {mode === "power" ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="up-x"
            label={strings.base}
            numeric
            onChange={(x) => setPower({ ...power, x })}
            value={power.x}
          />
          <TextField
            id="up-dx"
            label={strings.baseUncertainty}
            numeric
            onChange={(dx) => setPower({ ...power, dx })}
            value={power.dx}
          />
          <TextField
            id="up-n"
            label={strings.exponent}
            numeric
            onChange={(n) => setPower({ ...power, n })}
            value={power.n}
          />
        </div>
      ) : null}
      {mode === "formula" ? (
        <FormulaInputs
          formula={formula}
          setFormula={setFormula}
          setVariable={(name, patch) =>
            setVariables((current) => {
              // A variable typed for the first time has no entry yet.
              const existing: VariableInput =
                name in current
                  ? current[name]
                  : { value: "", uncertainty: "" };
              return { ...current, [name]: { ...existing, ...patch } };
            })
          }
          variables={variables}
        />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="up-rounding"
          label={strings.rounding}
          onChange={setRule}
          options={[
            { value: "one-or-two", label: strings.roundingOneOrTwo },
            { value: "two", label: strings.roundingTwo },
          ]}
          value={rule}
        />
        {mode === "formula" ? (
          <SelectField
            id="up-angle"
            label={strings.angleUnit}
            onChange={setUnit}
            options={[
              { value: "radians", label: strings.radians },
              { value: "degrees", label: strings.degrees },
            ]}
            value={unit}
          />
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome outcome={outcome} rule={rule} />
      </section>
    </>
  );
}
